import { subModuleNameMatches } from "services/utils/HelperFunction";
import {
  REIMBURSEMENT_PAY_INPUT_TYPE,
  requestReimbursementSettings,
} from "pages/PayRoll/Shared/reimbursementApi";

// Which sidebar rows a user may see, from their provisioning payload for a
// product. Shared by the sidebar and the top-bar search, so both always
// agree on what the user can open.

// Team Workspace > Approve flyout category name -> its backend
// sub_module_name under the "Approve" module_details entry. These don't all
// textually match the sidebar's own display names (e.g. "Permission" ->
// "Permissions", "Salary Advance" -> "Advance Salary", "Off-Day Duty" ->
// "Off Day Duty"), so an explicit map is needed instead of the direct
// name === module_name comparison submenuAccessible uses for top-level
// submenus. Categories with no entry here (currently none) always show.
const APPROVE_CATEGORY_SUBMODULE = {
  "Leave Requests": "Leaves",
  "Comp Off": "Comp Off",
  "Attendance Correction": "Attendance Correction",
  "Schedule Change": "Schedule Change",
  "Shift Change": "Shift Change",
  Permission: "Permissions",
  "On Duty": "On Duty",
  Break: "Break",
  Overtime: "Overtime",
  "Off-Day Duty": "Off Day Duty",
  "Salary Advance": "Advance Salary",
  "Expense Claims": "Expense",
  Reimbursement: "Expense",
};

// My Workspace > My Requests ("My Request" module_details entry) flyout
// category name -> its backend sub_module_name, same idea as
// APPROVE_CATEGORY_SUBMODULE above but for the "My Request" module (was
// "Apply" until a live payload confirmed the module itself was renamed).
// A later live payload also confirmed real, standalone submodules for
// Attendance Correction/Shift Change/On Duty/Break — they used to be left
// out of this map because the only evidence available then suggested they
// might share one combined "Myzone WorkAdjustment" submodule instead; that
// combined name still doesn't appear in any real payload, but each of these
// four now has its own confirmed exact-match entry below. Comp Off used to
// be left deliberately absent here on the theory that "no confirmed mapping
// found yet" should default to showing the row rather than guessing a name
// that could wrongly hide it -- but two separate live "My Request" payloads
// (Strco, company_id 1) have now been checked and neither ever includes a
// Comp Off submodule, so that absence isn't missing evidence, it's the
// confirmed answer: this company's role has no Comp Off access under My
// Request at all, and the row was showing regardless. The entry below maps
// it the same way as its siblings so it correctly hides now and will
// correctly start working the moment a payload actually provisions it.
//
// "Leave Requests"/Permission/"Salary Advance"/"Expense Claims" map to
// "Leaves"/"Permissions"/"Advance Salary"/"Expense" here, which look like a
// mismatch against a live payload's real names ("Leave Request",
// "Permission", "Salary Advance", "Expense Claims") -- but isFlyoutCategoryAccessible
// resolves these through subModuleNameMatches, which already treats each of
// those four as an alias of the other (see SUB_MODULE_NAME_ALIASES in
// HelperFunction.js: Leaves/Expense/"Advance Salary"/Permissions each list
// both spellings). So this mapping is correct as written; don't "fix" it to
// the live payload's exact spelling without also confirming no other tenant
// still returns the values below, which the alias table exists to cover.
const APPLY_CATEGORY_SUBMODULE = {
  "Leave Requests": "Leaves",
  "Attendance Correction": "Attendance Correction",
  "Schedule Change": "Schedule Change",
  "Shift Change": "Shift Change",
  Permission: "Permissions",
  "On Duty": "On Duty",
  "Comp Off": "Comp Off",
  Break: "Break",
  Overtime: "Myzone Overtime",
  "Off-Day Duty": "Off Day Duty",
  "Salary Advance": "Advance Salary",
  "Expense Claims": "Expense",
  Reimbursement: "Expense",
};

// Which category map applies depends on the section's own name ("Approvals"
// for Team Workspace, "My Request" for My Workspace's "My Requests") — see
// isSectionRowAccessible below. Any section not listed here falls through to
// no filtering at all (every row shows), same as an unmapped category within
// one of the tables above.
// Administration > Organisation flyout row name -> its backend
// sub_module_name under the "Organisation" module.
const ORGANISATION_CATEGORY_SUBMODULE = {
  "Organisation Details": "Organisation Details",
  Contacts: "Contacts",
  Devices: "Devices",
  Settings: "Settings",
};

// Team Workspace > Settings flyout row name -> its backend sub_module_name
// under Team Workspace's own "Settings" module. "Check in Policy" goes
// through the alias table in HelperFunction.js, which also accepts the
// backend's "Check In Policy" spelling.
const TEAM_SETTINGS_CATEGORY_SUBMODULE = {
  "Check-In Policy": "Check in Policy",
  "Geo Fenced Sites": "Geo Fenced Sites",
};

export const FLYOUT_CATEGORY_SUBMODULE_BY_MODULE = {
  Approvals: APPROVE_CATEGORY_SUBMODULE,
  "My Request": APPLY_CATEGORY_SUBMODULE,
  Organisation: ORGANISATION_CATEGORY_SUBMODULE,
  Settings: TEAM_SETTINGS_CATEGORY_SUBMODULE,
};

// Rows are shown for every module in the product's provisioning payload,
// whatever its viewer/has_access flags: opening a screen without access
// shows the "right workspace" screen (ProductAccessGate) instead.
//
// Redux holds one product's payload at a time. For these products a row
// only counts once their own payload has arrived (see submenuAccessible).
export const STRICT_PRODUCT_MATCH_PARENTS = new Set([
  "Monitoring",
  "System Configuration",
]);

// A product's section row (Approvals, Salary, ...) is shown when its module
// is in the payload. `access` is that product's provisioning payload.
export const isSubmenuAccessible = (productName, submenu, access) => {
  // Redux holds one product's payload at a time. Until Monitoring's or
  // System Configuration's own payload arrives, the previous product's
  // modules must not decide their rows: their module names ("Devices",
  // "Dashboard") also exist in other products.
  if (STRICT_PRODUCT_MATCH_PARENTS.has(productName) && access?.product_name !== productName) {
    return false;
  }
  // Placeholder rows for screens the backend doesn't provision yet
  // (e.g. Payroll > Statutory) opt out of the module check explicitly.
  if (submenu?.skipAccessCheck) return true;
  const moduleName = submenu?.permissionName || submenu?.name;
  return Boolean(access?.module_details?.find((module) => module.module_name === moduleName));
};

// A child row is hidden when its submodule isn't in the payload. The
// section-name -> submodule maps above are needed because the sidebar labels
// don't all match the backend names. Unmapped rows always show.
export const isSectionRowAccessible = (submenu, nested, access) => {
  const subModuleName = (FLYOUT_CATEGORY_SUBMODULE_BY_MODULE[submenu?.name] || {})[nested?.name];
  if (!subModuleName) return true;
  const submodules = access?.module_details?.find(
    (module) => module?.module_name === submenu?.name,
  )?.submodules;
  return Boolean(submodules?.find((sub) => subModuleNameMatches(sub?.sub_module_name, subModuleName)));
};

// Pay Inputs rows that Pay Input Configuration can switch off: the sidebar
// hides them, and so does the search, while their pay input is off.
export const CONFIG_GATED_FLYOUT_ITEMS = [
  { path: "/admin/payroll/pay-inputs/shift-earnings", payInputType: "SHIFT_EARNING" },
  { path: "/admin/payroll/pay-inputs/overtime", payInputType: "OVERTIME" },
  { path: "/admin/payroll/pay-inputs/leave", payInputType: "LEAVE" },
  { path: "/admin/payroll/pay-inputs/penalties", payInputType: "PENALTY" },
  { path: "/admin/payroll/pay-inputs/loans-and-advances", payInputType: "LOAN_ADVANCE" },
  // Reimbursements has its own settings API; `request` loads it instead.
  {
    path: "/admin/payroll/pay-inputs/reimbursements",
    payInputType: REIMBURSEMENT_PAY_INPUT_TYPE,
    request: requestReimbursementSettings,
  },
];
